import type { InferType } from 'groqd'
import { useLiveQuery } from 'next-sanity/preview'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import type { ParsedUrlQuery } from 'node:querystring'
import { useMemo } from 'react'

import type { Language, SupportedMarketKey } from '@nxt-insurance/constants'

import type { Navbar, Page as PageType, RouteWithPage } from '@/cms/pages'
import { routeQueryPreview } from '@/cms/pages'
import { PageMetaDescription } from '@/components/PageMetaDescription'
import QrCodeOverlay from '@/components/QrCodeOverlay'
import { SectionsRenderer } from '@/components/renderers/Sections'
import Header from '@/components/sections/Header'
import type { AlternateLink } from '@/hooks/useAlternateLinks'
import Spinner from '@ui-kit/components/Spinner'

import { PageHead } from './PageHead'

const Footer = dynamic(async () => import('@/components/sections/Footer').then((mod) => mod.Footer))

const getRouteWithPage = (
  route: NonNullable<InferType<typeof routeQueryPreview>>,
  query: ParsedUrlQuery
): RouteWithPage => {
  const routePage = route.page

  let page: PageType | undefined
  if (routePage._type === 'website.richContent.page' && '_id' in routePage) {
    page = routePage
  } else if (routePage._type === 'website.richContent.page.ab.test' && '_id' in routePage) {
    const treatment = query[routePage.featureFlag]
    const variation =
      routePage.variations.find((v) => v.name === treatment) ??
      routePage.variations[Math.floor(Math.random() * routePage.variations.length)]
    if (!variation) {
      throw new Error(
        `Assertion: no treatment matched ${treatment} for the experiment ${routePage.title}`
      )
    }
    page = variation.page
  }
  if (page) {
    return {
      ...route,
      page,
    }
  }
  throw new Error('Assertion: Page is unsupported')
}

export const PageWithPreview = (props: {
  navbar?: Navbar
  alternateLinks: AlternateLink[]
  slug: string
  folder?: string
  lang: Language
  market: SupportedMarketKey
}) => {
  const { query } = useRouter()

  const [routeFromPreview, loading] = useLiveQuery(null, routeQueryPreview.query, {
    lang: props.lang,
    market: props.market,
    slug: props.slug,
    folder: props.folder ?? null,
  })

  const route: RouteWithPage | null = useMemo(
    () => (routeFromPreview ? getRouteWithPage(routeFromPreview, query) : null),
    [routeFromPreview, query]
  )
  if (loading || !route) {
    return <Spinner fullScreen />
  }

  return (
    <>
      <PageHead alternateLinks={props.alternateLinks} disallowRobots={route.disallowRobots} />
      <QrCodeOverlay />

      <PageMetaDescription metaDescription={route.page.metaDescription} />

      {route.pageType?._type !== 'website.pageType.noHeaderNoFooter' ? (
        <Header
          pageType="regular"
          navbar={props.navbar}
          routePageType={route.pageType}
          alternateLinks={props.alternateLinks}
        />
      ) : null}
      <SectionsRenderer sections={route.page?.sections ?? []} />
      {route.pageType?._type !== 'website.pageType.noHeaderNoFooter' ? (
        <Footer footerColor={route.page.footerColor ?? undefined} pageType={route.pageType} />
      ) : null}
    </>
  )
}
